import { setTokenOnHeader } from '@repo/services/api';
import { isPopupCancelledByUser } from '@repo/services/firebase';
import { CreateUserRequestDto, getCustomToken } from '@repo/services/hooks';
import { airbridge } from '@repo/services/sdk';
import {
  GoogleAuthProvider,
  OAuthCredential,
  OAuthProvider,
  User,
  UserCredential,
  signInWithCustomToken,
  signInWithPopup,
} from 'firebase/auth';
import { t } from 'i18next';

import { postRegisterUser } from 'services/api/auth';
import { auth, firebaseEvent } from 'services/firebase';
import http, { axiosInstance } from 'services/http';
import { handleFailToast } from 'utils/toast/handleToast';
import { showAuthErrorToast } from 'utils/toast/showAuthErrorToast';

import { queryClient } from 'components/provider/GlobalProvider';

import { setTokenCookie } from './tokenCookie';

/**
 * 파이어베이스에 로그인을 하기 위한 함수
 */
export async function authenticateToBubbleTapServer(customToken: string) {
  const cookieDomain = import.meta.env.VITE_APP_COOKIE_DOMAIN;

  signInWithCustomToken(auth, customToken)
    .then(async (userCredential: UserCredential) => {
      const token = await userCredential.user.getIdToken();
      // setLoading(false);
      queryClient.invalidateQueries();
      // guard component 를 위한 셋팅
      localStorage.setItem('isCustomLogin', 'true');
      window.dispatchEvent(new Event('loggedIn'));
      setTokenCookie(token, cookieDomain);

      // airbridge signin sdk
      airbridge.events.send('airbridge.user.signin');
    })
    .catch(() => {
      handleFailToast(t('login.account_fail_toast'));
    });
}

/**
 * 유저를 등록하거나 인증 처리를 해주는 함수
 * - 이미 등록된 유저 -> 로그인 성공
 * - 등록되지 않은 유저 -> 유저 등록 후, 로그인 처리
 */
async function authenticateOrRegisterUser(credential: OAuthCredential, user: User) {
  // const accessToken = credential.idToken;

  // 버블탭 서버에서 확인하기 위한 id Token을 파이어베이스로부터 받아온다.
  const idToken = await user.getIdToken();
  setTokenOnHeader(axiosInstance, auth, idToken);
  /** 2. call getCustomToken(/auth/token) to check registration on server
   * 2-1-1. statusCode 200 - this user is registered 등록된 유저
   * 2-1-2  inform to server with CustomToken - customToken 로그인
   * 2-2-1. statusCode 403 - unregistered  미등록 유저
   * 2-2-2. request user Creation - 신규 등록
   * */

  try {
    // 버블탭 서버에 이미 인증된 유저인지 판별 요청을 보냄
    const { customToken, isRegisteredOnServer } = await checkIsExistUserOnServer();

    if (isRegisteredOnServer && customToken) {
      // 이미 인증된 경우 -> 로그인
      await authenticateToBubbleTapServer(customToken);
    } else {
      showAuthErrorToast();
    }
  } catch (error: any) {
    const statusCode = error.response.data.statusCode;
    // 차단 코드도 statusCode 와 같이 응답 body 에 온다(useKaKaoLogin 과 동일)
    const isBlockedUser = error.response.data.code === 'BLOCKED';
    // 403 에러 발생 시, 서버에 유저가 등록되지 않은 상태
    const isNewUser = statusCode === 403 && !isBlockedUser;

    try {
      if (isNewUser) {
        // 등록 후 로그인
        await registerNewUser(user);
        await authenticateAfterRegisterNewUser();
      } else {
        showAuthErrorToast(error);
      }
    } catch (error) {
      showAuthErrorToast(error);
    }
  }
}

/**
 * 이미 유저가 서버에 존재하는지 파악
 */

export async function checkIsExistUserOnServer() {
  const res = await getCustomToken(http);
  const customToken = res?.token;
  const isRegisteredOnServer = !!customToken;

  return { isRegisteredOnServer, customToken };
}

export interface CreateUserResponseDto {
  /** @example 12345 */
  id: string;

  /** @example https://api.bubbletap.com/users/12345 */
  self: string;
}
/**
 * 신규 유저를 등록해주는 함수
 */
export async function registerNewUser(user: User) {
  const { uid, providerData } = user;
  const urlSearch = new URLSearchParams(window.location.search);
  const source = urlSearch.get('source');

  try {
    let reqBody = {
      externalId: uid,
      role: { code: 'USER' },
      displayLanguageCode: 'ko',
      storyLanguageCode: 'ko',
    } as CreateUserRequestDto;
    if (source) reqBody = { ...reqBody, source };

    const { id: userId } = await postRegisterUser(reqBody);

    // 이벤트 등록 (파이어베이스 및 twitter)
    airbridge.events.send('airbridge.user.signup');

    const getProviderCode = (providerId: string) => {
      switch (providerId) {
        case 'apple.com':
          return 'APPLE';
        case 'google.com':
          return 'GOOGLE';
        default:
          return 'BUBBLETAP';
      }
    };
    const providerId = providerData[0].providerId;
    const providerCode = getProviderCode(providerId);
    firebaseEvent.signUp({ userId, firebaseUserId: uid, providerCode });

    twtPixelForRegister();
    // await authenticateAfterRegisterNewUser();
  } catch (error: any) {
    handleFailToast(t('general.contact_customer_support_error_message'));
  }
}

/**
 * twitter pixel을 사용하여 특정 이벤트를 측정하고 추적하는데 사용
 */
function twtPixelForRegister() {
  // twitter pixel code ( 가입 측정 )
  const { twq } = window;
  twq('event', 'tw-oc463-oc515', {
    value: 1, // use this to pass the value of the conversion (e.g. 5.00)
    status: 'completed', // use this to pass the status of a sign up or subscription, (e.g. 'started' or 'completed')
    // email_address: '', // use this to pass a user’s email address
  });
}

/**
 * 유저를 등록하고 나서 로그인을 시키는 함수
 */
async function authenticateAfterRegisterNewUser() {
  try {
    const { customToken } = await checkIsExistUserOnServer();
    if (customToken) {
      authenticateToBubbleTapServer(customToken);
    }
  } catch (err: any) {
    handleFailToast(t('general.contact_customer_support_error_message'));
  }
}

type ProviderConstructor = typeof GoogleAuthProvider | typeof OAuthProvider;

type AuthProviderType = 'Google' | 'Apple';
/**
 * Provider 생성 함수
 * @param providerConstructor Provider 생성자 (GoogleAuthProvider | OAuthProvider)
 * @returns Firebase Provider 인스턴스
 */
function createProviderInstance(authType: AuthProviderType): {
  provider: InstanceType<ProviderConstructor>;
  constructor: ProviderConstructor;
} {
  if (authType === 'Apple') {
    return { provider: new OAuthProvider('apple.com'), constructor: OAuthProvider };
  }
  const googleProvider = new GoogleAuthProvider();
  const appEnv = import.meta.env.VITE_APP_ENV;
  if (appEnv !== 'production') {
    googleProvider.setCustomParameters({ prompt: 'select_account' });
  }
  return { provider: googleProvider, constructor: GoogleAuthProvider };
}

export async function socialLoginWith(authType: AuthProviderType) {
  //  1. login with firebase
  try {
    const { provider, constructor } = createProviderInstance(authType);

    // 2. Firebase Popup 로그인
    const result = await signInWithPopup(auth, provider);

    // 3. 로그인 성공: 사용자 인증 처리
    const credential = constructor.credentialFromResult(result);
    const user = result.user;

    if (credential && user) {
      await authenticateOrRegisterUser(credential, user);
    } else {
      throw new Error('Credential or user data is missing');
    }
  } catch (error: any) {
    // 유저가 팝업을 닫거나 다시 눌러 취소한 것은 실패가 아니다 — 토스트 없이 끝낸다(#2699).
    if (isPopupCancelledByUser(error)) return;
    console.error('social Login error: ', error);
    showAuthErrorToast();
  }
}
